import { uniqBy } from 'lodash';
import moment from 'moment-timezone';

import { lessonBasicResponseToAgendaEntry } from '../../utils/mappings/agendaMappings';
import { LessonBasicResponseToClassbookV2OverviewMyCourseEntry } from '../../utils/mappings/myCoursesMappings';
import {
  classServiceResponseToClassService,
  classServiceToClassServiceEntry,
  classServiceToClassServicePutEntry,
} from '../../utils/mappings/servicesMappings';
import { Paginated } from '../../utils/paginated';
import {
  ClassbookV2OverviewMyCourseEntry,
  Grade,
  GradeClass,
  MNSproCloudApi,
} from '..';
import {
  BoardPosition,
  ClassBasicResponse,
  ClassbookEntryCreateRequest,
  ClassbookEntryResponse,
  ClassbookEntryUpdateRequest,
  ClassResponse,
  CourseBasicResponse,
  CourseResponse,
  EffectiveLessonPermissions,
  GroupInformation,
  GroupRole,
  HomeworkResponse,
  LessonResponse,
  LessonsSignMultipleResponse,
  LessonThemeFilterType,
  LessonThemeResponse,
  Member,
  Origin,
  Person,
  SeatingPlanEntry,
  SeatingPlanNameResponse,
  SeatingPlanResponse,
  SeatingPlanUpdateEntry,
  SignLevel,
  Substitution,
  UnsignedFilterType,
} from '../api/v2';
import {
  classBasicResponseToGradeClass,
  classResponseToGradeClass,
  gradeBasicResponseToGrade,
  gradeResponseToGrade,
} from '../settings/classbook/ClassbookSettingsService.utils';
import { AgendaEntry } from './AgendaEntry.interface';
import {
  ClassbookV2CreateOrUpdateHomeworkEntry,
  ClassbookV2OverviewHomeworkEntry,
} from './homework.interfaces';
import { ClassService } from './services.interfaces';

const getClassregisterApi = (authToken: string) => {
  const config = MNSproCloudApi.V2.createConfiguration(authToken);
  return MNSproCloudApi.V2.ClassRegisterApiFactory(config);
};

export const ClassbookV2Service = {
  /**
   * Gets the course students of the given course.
   *
   * @note: This method allows to read azure group members without being a
   * member of the group but having the permission to read the classbook
   * course (i.e. a substitution teacher).
   *
   * @param authToken
   * @param organizationId
   * @param courseId
   * @param timePeriod
   * @returns
   */
  async getCourseStudents(
    authToken: string,
    organizationId: string,
    courseId: string,
    timePeriod?: string,
  ): Promise<Person[]> {
    const api = getClassregisterApi(authToken);
    const result = await api.getCourseStudents(
      courseId,
      organizationId,
      timePeriod,
    );

    const students: Person[] = result.data.map(m => ({
      id: m.id,
      displayName: m.displayName,
      firstName: m.firstName,
      lastName: m.lastName,
      mailAddress: m.mailAddress,
    }));

    return students;
  },

  async getLessonContent(
    authToken: string,
    organizationId: string,
    courseId: string,
    dayStart?: string,
    dayEnd?: string,
  ): Promise<LessonThemeResponse[]> {
    const api = getClassregisterApi(authToken);
    const result = await api.getAllLessonThemesByGroup(
      dayEnd ?? moment().toISOString(),
      dayStart ?? moment().toISOString(),
      courseId,
      organizationId,
    );

    return result.data;
  },

  async getHomeworkStatsCard(
    authToken: string,
    organizationId: string,
    courseId: string,
  ): Promise<HomeworkResponse[]> {
    const api = getClassregisterApi(authToken);
    const result = await api.getAllHomeworkByCourse(courseId, organizationId);

    return result.data;
  },

  async addLessonContent(
    authToken: string,
    organizationId: string,
    lessonId: string,
    content: string,
    methodOfTeachingId: string,
  ): Promise<LessonThemeResponse> {
    const api = getClassregisterApi(authToken);
    const result = await api.createLessonTheme(organizationId, {
      lessonId,
      method: {
        id: methodOfTeachingId,
      },
      themeDescription: content,
    });

    return result.data;
  },

  async updateLessonContent(
    authToken: string,
    organizationId: string,
    lessonId: string,
    contentId: string,
    content: string,
    methodOfTeachingId: string,
  ): Promise<LessonThemeResponse> {
    const api = getClassregisterApi(authToken);
    const result = await api.updateLessonTheme(organizationId, {
      lessonId,
      id: contentId,
      method: {
        id: methodOfTeachingId,
      },
      themeDescription: content,
    });

    return result.data;
  },

  async deleteLessonContent(
    authToken: string,
    organizationId: string,
    contentId: string,
  ): Promise<void> {
    const api = getClassregisterApi(authToken);
    await api.deleteLessonTheme(contentId, organizationId);
  },

  async getClasses(
    authToken: string,
    organizationId: string,
  ): Promise<ClassBasicResponse[]> {
    const api = getClassregisterApi(authToken);
    const result = await api.getAllBasicClasses(organizationId);
    return result.data.value;
  },

  async getClassesPaginated(
    authToken: string,
    organizationId: string,
    pageSize?: number,
    continuationToken?: string,
  ): Promise<Paginated<ClassBasicResponse>> {
    const api = getClassregisterApi(authToken);

    const result = await api.getAllBasicClasses(
      organizationId,
      undefined,
      continuationToken,
      pageSize,
    );

    const { value, nextLink, totalCount } = result.data;
    return {
      value,
      nextLink: nextLink === null ? undefined : nextLink,
      totalCount,
    };
  },

  async getCourses(
    authToken: string,
    organizationId: string,
  ): Promise<CourseBasicResponse[]> {
    const api = getClassregisterApi(authToken);

    const result = await api.getAllBasicCourses(organizationId);
    return result.data.value;
  },

  async getCourseDetails(
    authToken: string,
    organizationId: string,
    courseId: string,
  ): Promise<CourseResponse> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const result = await api.getLatestCourse(courseId, organizationId);
    return result.data;
  },

  async getCoursesPaginated(
    authToken: string,
    organizationId: string,
    pageSize?: number,
    continuationToken?: string,
  ): Promise<Paginated<CourseBasicResponse>> {
    const api = getClassregisterApi(authToken);

    const result = await api.getAllBasicCourses(
      organizationId,
      undefined,
      continuationToken,
      pageSize,
    );

    const { value, nextLink, totalCount } = result.data;
    return {
      value,
      nextLink: nextLink === null ? undefined : nextLink,
      totalCount,
    };
  },

  async getClass(
    authToken: string,
    organizationId: string,
    classId: string,
  ): Promise<ClassResponse> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const result = await api.getLatestClassWithBasicCourses(
      classId,
      organizationId,
    );

    return result.data;
  },

  async getMyCourses(
    authToken: string,
    organizationId: string,
    period: string | undefined,
  ): Promise<ClassbookV2OverviewMyCourseEntry[]> {
    const api = getClassregisterApi(authToken);
    const myCoursesResponse = await api.getLessonsOfMyCourses(
      organizationId,
      new Date().toISOString(),
      period,
      undefined,
      undefined,
      undefined,
      {
        validateStatus: (status: number) => {
          return (status >= 200 && status <= 299) || status === 404;
        },
      },
    );

    if (myCoursesResponse.status === 404) {
      // 404 will be returned if for example the requesting user is not member
      // of the requested organization id.
      return [];
    }

    return myCoursesResponse.data.value.map(
      LessonBasicResponseToClassbookV2OverviewMyCourseEntry,
    );
  },

  async getAgenda(
    authToken: string,
    organizationId: string,
    dayStart: string,
  ): Promise<AgendaEntry[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const requestResult = await api.getLessonsOfMyAgenda(
      organizationId,
      dayStart,
    );
    const agendaEntries: AgendaEntry[] = requestResult.data.value.map(
      lessonBasicResponseToAgendaEntry,
    );
    return agendaEntries;
  },

  async getLessonPermissions(
    authToken: string,
    organizationId: string,
    lessonId: string,
  ): Promise<EffectiveLessonPermissions> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const result = await api.getMyLessonPermissions(lessonId, organizationId);

    return result.data;
  },

  async getCourseClassbookEntries(
    authToken: string,
    organizationId: string,
    courseId: string,
    dayStart: string,
    dayEnd: string,
  ): Promise<ClassbookEntryResponse[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const result = await api.getAllClassbookEntriesByGroup(
      dayEnd,
      courseId,
      organizationId,
      dayStart,
    );

    return result.data;
  },

  async addCourseClassbookEntry(
    authToken: string,
    organizationId: string,
    courseId: string,
    definitionId: string,
    affectedUsers: string[],
    comment: string,
    date: string,
    lessonId: string,
  ): Promise<ClassbookEntryResponse> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const request: ClassbookEntryCreateRequest = {
      lessonId,
      groupId: courseId,
      definition: {
        id: definitionId,
      },
      occured: date,
      affectedUsers,
      comment,
    };

    const result = await api.createClassbookEntry(organizationId, request);

    return result.data;
  },

  async updateClassbookEntry(
    authToken: string,
    organizationId: string,
    entryId: string,
    courseId: string,
    definitionId: string,
    affectedUsers: string[],
    comment: string,
    date: string,
    lessonId: string,
  ): Promise<ClassbookEntryResponse> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const request: ClassbookEntryUpdateRequest = {
      lessonId,
      id: entryId,
      groupId: courseId,
      definition: {
        id: definitionId,
      },
      occured: date,
      affectedUsers,
      comment,
    };

    const result = await api.updateClassbookEntry(organizationId, request);

    return result.data;
  },

  async removeClassbookEntry(
    authToken: string,
    organizationId: string,
    entryId: string,
  ): Promise<void> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    await api.deleteClassbookEntry(entryId, organizationId);
  },

  /**
   * @todo pagination
   * @todo implement real api call
   */
  async getClassLessons(
    authToken: string,
    organizationId: string,
    classId: string,
    start: string,
    end: string,
  ): Promise<LessonResponse[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const response = await api.getLessonsOfClassRange(
      classId,
      end,
      organizationId,
      start,
    );

    return response.data.value;
  },

  async getAllCourses(
    authToken: string,
    organizationId: string,
  ): Promise<CourseBasicResponse[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.getAllBasicCourses(organizationId);

    return result.data.value;
  },

  async getGrade(
    authToken: string,
    gradeId: string,
    organizationId: string,
  ): Promise<Grade> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.getLatestGradeWithBasicClasses(
      gradeId,
      organizationId,
    );

    return gradeResponseToGrade(result.data);
  },

  async getGrades(authToken: string, organizationId: string): Promise<Grade[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.getAllBasicGrades(organizationId);

    return result.data.value.map(gradeBasicResponseToGrade);
  },

  async getGradeClassById(
    authToken: string,
    organizationId: string,
    classId: string,
  ): Promise<GradeClass> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.getLatestClassWithBasicCourses(
      classId,
      organizationId,
    );

    return classResponseToGradeClass(result.data);
  },

  async getGradeClasses(
    authToken: string,
    organizationId: string,
    gradeId: string,
  ): Promise<GradeClass[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.getLatestGradeWithBasicClasses(
      gradeId,
      organizationId,
    );

    return result.data.gradeClasses?.map(classBasicResponseToGradeClass) ?? [];
  },

  /**
   * @todo pagination
   * @todo implement real api call
   */
  async getGradeLessons(
    authToken: string,
    organizationId: string,
    gradeId: string,
    start: string,
    end: string,
  ): Promise<LessonResponse[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    // const response = await api.getLessonsOfGradeRange(
    //   end,
    //   gradeId,
    //   organizationId,
    //   start,
    //   undefined,
    //   5,
    //   5,
    // );

    // let nextLink = response.data.nextLink?.replace('http://', 'https://');
    // let lessons: LessonResponse[] = response.data.value;

    // while (nextLink) {
    //   const pageResponse = await axios.get<PaginatedOfLessonResponse>(
    //     nextLink,
    //     {
    //       headers: {
    //         authorization: 'Bearer ' + authToken,
    //       },
    //     },
    //   );

    //   lessons = lessons.concat(pageResponse.data.value);
    //   nextLink = pageResponse.data.nextLink?.replace('http://', 'https://');
    // }

    // return lessons;

    const response = await api.getLessonsOfGradeRange(
      end,
      gradeId,
      organizationId,
      start,
    );

    return response.data.value;
  },

  async getServicesByLessonId(
    authToken: string,
    organizationId: string,
    lessonId: string,
  ): Promise<ClassService[]> {
    const api = getClassregisterApi(authToken);

    const result = await api.getClassServicesOfLesson(lessonId, organizationId);
    return result.data.map(classServiceResponseToClassService);
  },

  async createService(
    authToken: string,
    organizationId: string,
    newService: ClassService,
  ): Promise<ClassService> {
    const api = getClassregisterApi(authToken);
    const result = await api.createClassService(
      organizationId,
      classServiceToClassServiceEntry(newService),
    );
    return classServiceResponseToClassService(result.data);
  },

  async updateService(
    authToken: string,
    organizationId: string,
    updatedService: ClassService,
  ): Promise<ClassService> {
    const api = getClassregisterApi(authToken);
    const result = await api.updateClassService(
      organizationId,
      classServiceToClassServicePutEntry(updatedService),
    );
    return classServiceResponseToClassService(result.data);
  },

  async deleteService(
    authToken: string,
    organizationId: string,
    serviceId: string,
  ): Promise<void> {
    const api = getClassregisterApi(authToken);
    await api.deleteClassService(organizationId, serviceId);
  },

  async getAllHomeworkByGroup(
    authToken: string,
    organizationId: string,
    courseId: string,
    dayEnd: string,
    dayStart: string,
  ): Promise<ClassbookV2OverviewHomeworkEntry[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.getAllHomeworkByCourse(
      courseId,
      organizationId,
      dayEnd,
      dayStart,
    );
    return result.data as ClassbookV2OverviewHomeworkEntry[];
  },

  async getAllHomeworkByOrganization(
    authToken: string,
    organizationId: string,
    dayEnd: string,
    dayStart: string,
  ): Promise<ClassbookV2OverviewHomeworkEntry[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const result = await api.getAllHomework(dayEnd, dayStart, organizationId);

    return result.data as ClassbookV2OverviewHomeworkEntry[];
  },

  async saveHomework(
    authToken: string,
    organizationId: string,
    homework: ClassbookV2CreateOrUpdateHomeworkEntry,
  ): Promise<ClassbookV2OverviewHomeworkEntry> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const result = await api.createHomework(organizationId, {
      ...homework,
    });
    return result.data as ClassbookV2OverviewHomeworkEntry;
  },

  async updateHomework(
    authToken: string,
    organizationId: string,
    homework: ClassbookV2CreateOrUpdateHomeworkEntry,
  ): Promise<ClassbookV2OverviewHomeworkEntry> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const result = await api.updateHomework(organizationId, { ...homework });
    return result.data as ClassbookV2OverviewHomeworkEntry;
  },

  async deleteHomework(
    authToken: string,
    organizationId: string,
    homeworkId: string,
  ): Promise<boolean> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const result = await api.deleteHomework(homeworkId, organizationId);
    return result.data;
  },

  async signLesson(
    authToken: string,
    organizationId: string,
    lessonId: string,
    signLevel: SignLevel,
  ): Promise<LessonResponse> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.signLesson(lessonId, organizationId, signLevel);
    return result.data;
  },

  async unsignLesson(
    authToken: string,
    organizationId: string,
    lessonId: string,
    signLevel: SignLevel,
  ): Promise<LessonResponse> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.unSignLesson(lessonId, organizationId, signLevel);
    return result.data;
  },

  async signMultipleLessons(
    authToken: string,
    organizationId: string,
    timePeriod?: string,
    groupId?: string,
    userId?: string,
    from?: string,
    to?: string,
    filterType?: UnsignedFilterType,
    signLevel?: SignLevel,
    filterLessonTheme?: LessonThemeFilterType,
    options?: any,
  ): Promise<LessonsSignMultipleResponse> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const result = await api.signMultipleLessons(
      organizationId,
      timePeriod,
      groupId,
      userId,
      from,
      to,
      filterType,
      signLevel,
      filterLessonTheme,
      options,
    );

    return result.data;
  },

  /**
   *
   *
   * @static
   * @param {string} authToken
   * @param {string} organizationIds
   * @param {string} groupId
   * @return {*}  {Promise<SeatingPlanResponse>}
   */
  async getSeatingPlanForGroup(
    authToken: string,
    organizationId: string,
    groupId: string,
  ): Promise<SeatingPlanResponse> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const result = await api.getSeatingPlanByGroup(
      groupId,
      organizationId,
      undefined,
      {
        validateStatus: (status: number) => {
          return (status >= 200 && status <= 299) || status === 404;
        },
      },
    );

    if (result.status === 404) {
      return {
        id: '',
        name: '',
        board: BoardPosition.top,
        dimensionX: 10,
        dimensionY: 10,
        seats: [],
      };
    }

    return result.data;
  },
  /**
   *
   *
   * @static
   * @param {string} authToken
   * @param {SeatingPlanUpdateRequest} seatingPlanUpdateRequest
   * @return {*}  {Promise<SeatingPlanResponse>}
   */
  async updateSeatingPlanForGroup(
    authToken: string,
    seatingPlanUpdate: SeatingPlanUpdateEntry,
    organizationId: string,
  ): Promise<SeatingPlanResponse> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const result = await api.updateSeatingPlan(
      organizationId,
      seatingPlanUpdate,
    );
    return result.data;
  },

  /**
   *
   *
   * @static
   * @param {string} authToken
   * @param {string} organizationId
   * @param {string} seatingPlanToDelete
   * @return {*}  {Promise<SeatingPlanResponse>}
   */
  async deleteSeatingPlan(
    authToken: string,
    organizationId: string,
    seatingPlanId: string,
  ): Promise<boolean> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const result = await api.deleteSeatingPlan(organizationId, seatingPlanId);

    return true;
  },

  /**
   *
   *
   * @static
   * @param {string} authToken
   * @param {SeatingPlanUpdateRequest} seatingPlanUpdateRequest
   * @return {*}  {Promise<SeatingPlanResponse>}
   */
  async createSeatingPlanForGroup(
    authToken: string,
    seatingPlanCreate: SeatingPlanEntry,
    organizationId: string,
  ): Promise<SeatingPlanResponse> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const result = await api.createSeatingPlan(
      organizationId,
      seatingPlanCreate,
    );
    return result.data;
  },
  /**
   *
   *
   * @static
   * @param {string} authToken
   * @param {string} organizationId
   * @return {*}  {Promise<SeatingPlanResponse>}
   * @memberof ClassBookService
   */
  async getSeatingPlans(
    authToken: string,
    organizationId: string,
  ): Promise<SeatingPlanNameResponse[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const result = await api.getAllSeatingPlans(organizationId);
    return result.data;
  },

  /**
   * Returns all groups of the provided organization/tenant.
   * If origin is "AixConcept". Only groups of the given or current time period will be returned by default.
   *
   * @static
   * @param {string} authToken
   * @param {string} organizationId
   * @param {Origin} origin
   * @param {string} [timePeriod]
   * @param {GroupRole} [role]
   * @return {*}  {Promise<GroupInformation[]>}
   * @memberof ClassbookV2Service
   */
  async getAzureAdGroups(
    authToken: string,
    organizationId: string,
    origin: Origin,
    timePeriod?: string,
    role?: GroupRole,
  ): Promise<GroupInformation[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const result = await api.getAzureGroups(
      organizationId,
      origin,
      role,
      timePeriod,
    );

    return result.data;
  },

  /**
   * Returns the requested group id.
   * Users won't be returned by default.
   *
   * @static
   * @param {string} authToken
   * @param {string} organizationId
   * @param {string} groupId
   * @param {boolean} [includeMembers]
   * @return {*}  {Promise<GroupInformation>}
   * @memberof ClassbookV2Service
   */
  async getAzureAdGroup(
    authToken: string,
    organizationId: string,
    groupId: string,
    includeMembers?: boolean,
  ): Promise<GroupInformation> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const result = await api.getAzureGroup(
      groupId,
      organizationId,
      includeMembers,
    );

    return result.data;
  },

  /**
   * Returns the all members and owners of the
   * requested groupId.
   *
   * @static
   * @param {string} authToken
   * @param {string} organizationId
   * @param {string} groupId
   * @return {*}  {Promise<Member[]>}
   * @memberof ClassbookV2Service
   */
  async getAzureAdGroupUsers(
    authToken: string,
    organizationId: string,
    groupId: string,
  ): Promise<Member[]> {
    const group = await ClassbookV2Service.getAzureAdGroup(
      authToken,
      organizationId,
      groupId,
      true,
    );

    const all: Member[] = [...(group.users ?? []), ...(group.owners ?? [])];

    return uniqBy(all, member => member.id);
  },

  async getSubstitutions(
    apiKey: string,
    start?: string,
  ): Promise<Substitution[]> {
    const config = MNSproCloudApi.V2.createConfiguration(
      apiKey,
      undefined,
      'api',
    );

    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const response = await api.getSubstitutions(start);

    return response.data;
  },
};
